import { useMemo } from 'react';
import {
    IconArrowDown,
    IconArrowUp,
    IconCheck,
    IconCircle,
    IconCopy,
    IconCursorText,
    IconLine,
    IconLink,
    IconMessage2,
    IconPencil,
    IconPhoto,
    IconRectangle,
    IconSquare,
    IconTrash,
    IconX,
} from '@tabler/icons-react';
import { OBJ } from '../constants';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../utils/bnFormat';
import { findSelected, usePdfEditorStore } from '../state/usePdfEditorStore';
import { EDITOR_CHROME_ATTR, keepTextEditorFocus } from '../ui/prims';
import OptionsBar from '../toolbar/OptionsBar';
import Hint from '../../../components/common/Hint';

/** One glyph per kind of thing on a page, for the card at the top. */
const KIND_ICON = {
    [OBJ.TEXT]: IconCursorText,
    [OBJ.INK]: IconPencil,
    [OBJ.RECT]: IconRectangle,
    [OBJ.ELLIPSE]: IconCircle,
    [OBJ.LINE]: IconLine,
    [OBJ.ARROW]: IconLine,
    [OBJ.IMAGE]: IconPhoto,
    [OBJ.WHITEOUT]: IconSquare,
    [OBJ.CHECK]: IconCheck,
    [OBJ.CROSS]: IconX,
    [OBJ.NOTE]: IconMessage2,
    [OBJ.LINK]: IconLink,
};

/** A section of the panel: a small caption over whatever it introduces. */
function Section({ label, children }) {
    return (
        <div className="flex flex-col gap-[7px]">
            <div className="text-[10.5px] font-bold tracking-[0.04em] text-[#B8B1AA]">{label}</div>
            {children}
        </div>
    );
}

/**
 * The inspector: what is selected, and everything about it.
 *
 * It replaces the strip that used to run under the toolbar. A row 46px tall had
 * to put a font, a size, four toggles, two colours and an alignment control on
 * one line, so each was as small as it could be and the rarer ones scrolled off
 * the side; a column has room to label them and to say what they belong to.
 */
export default function InspectorPanel() {
    const { t } = useTranslation();

    const selectedId = usePdfEditorStore((s) => s.selectedId);
    const pages = usePdfEditorStore((s) => s.pages);
    const objectsByUid = usePdfEditorStore((s) => s.objectsByUid);
    const toggleInspector = usePdfEditorStore((s) => s.toggleInspector);
    const removeObject = usePdfEditorStore((s) => s.removeObject);
    const duplicateObject = usePdfEditorStore((s) => s.duplicateObject);
    const reorderObject = usePdfEditorStore((s) => s.reorderObject);

    const selection = useMemo(
        () => findSelected({ selectedId, pages, objectsByUid }),
        [selectedId, pages, objectsByUid],
    );
    const object = selection?.object || null;
    const uid = selection?.uid;
    // `findSelected` knows which page holds it; the panel wants to say which
    // number that is.
    const pageIndex = uid ? pages.findIndex((page) => page.uid === uid) : -1;

    const Glyph = (object && KIND_ICON[object.type]) || IconSquare;
    // A text box is worth naming by what it says; everything else has only its
    // kind to go on.
    const name = object?.type === OBJ.TEXT && object.text
        ? String(object.text).replace(/\s+/g, ' ').trim().slice(0, 40)
        : t(`pdfEditor.kinds.${object?.type}`);

    return (
        <aside
            {...{ [EDITOR_CHROME_ATTR]: '' }}
            // Clicking a control must not pull the caret out of an open text
            // box: the edit would end under the click.
            onMouseDown={keepTextEditorFocus}
            className="flex w-[266px] flex-none flex-col border-l border-[#ECE8E4] bg-white dark:border-zinc-800 dark:bg-zinc-900"
        >
            <div className="flex h-[34px] flex-none items-center justify-between border-b border-[#F0ECE8] px-[11px] dark:border-zinc-800">
                <span className="text-[12px] font-bold text-[#1C1815] dark:text-zinc-100">
                    {object ? t('pdfEditor.inspector.selected') : t('pdfEditor.inspector.title')}
                </span>
                <Hint label={t('common.close')}>
                    <button
                        type="button"
                        onClick={toggleInspector}
                        aria-label={t('common.close')}
                        className="flex h-[22px] w-[22px] items-center justify-center rounded-md text-[#A8A099] transition-colors hover:bg-[#F4F1ED] hover:text-[#3D3833] dark:hover:bg-zinc-800"
                    >
                        <IconX size={15} stroke={1.9} />
                    </button>
                </Hint>
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-[14px] overflow-y-auto px-[11px] pb-[18px] pt-3 [scrollbar-width:thin]">
                {object ? (
                    <div className="flex items-center gap-[9px] rounded-[10px] border border-[#F0ECE8] bg-[#FBFAF9] px-2.5 py-[9px] dark:border-zinc-800 dark:bg-zinc-800/50">
                        <span className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-lg bg-primary-50 text-primary-800 dark:bg-zinc-800 dark:text-primary-300">
                            <Glyph size={16} stroke={1.8} />
                        </span>
                        <div className="min-w-0 leading-[1.4]">
                            <div className="truncate text-[12.5px] font-bold text-[#1C1815] dark:text-zinc-100">{name}</div>
                            <div className="truncate text-[11px] text-[#8A827B] dark:text-zinc-400">
                                {t(`pdfEditor.kinds.${object.type}`)}
                                {pageIndex >= 0
                                    ? ` · ${t('pdfEditor.pageNumber', { number: toBnDigits(pageIndex + 1) })}`
                                    : ''}
                            </div>
                        </div>
                    </div>
                ) : null}

                {object ? (
                    <Section label={t('pdfEditor.inspector.format')}>
                        <OptionsBar stacked />
                    </Section>
                ) : (
                    // The armed tool may have settings of its own (a pencil's
                    // width, a highlighter's colour) or none at all, and only it
                    // knows which — so it is rendered bare, above the hint.
                    <OptionsBar stacked />
                )}

                {object ? (
                    <>
                        <Section label={t('pdfEditor.inspector.arrange')}>
                            <div className="flex gap-1.5">
                                <button
                                    type="button"
                                    onClick={() => reorderObject(uid, object.id, 'front')}
                                    className="flex h-[31px] flex-1 items-center justify-center gap-1.5 rounded-lg border border-[#E2DCD6] bg-white text-[11.5px] font-semibold text-[#3D3833] transition-colors hover:border-primary/50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                                >
                                    <IconArrowUp size={14} stroke={1.9} />
                                    {t('pdfEditor.options.bringFront')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => reorderObject(uid, object.id, 'back')}
                                    className="flex h-[31px] flex-1 items-center justify-center gap-1.5 rounded-lg border border-[#E2DCD6] bg-white text-[11.5px] font-semibold text-[#3D3833] transition-colors hover:border-primary/50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                                >
                                    <IconArrowDown size={14} stroke={1.9} />
                                    {t('pdfEditor.options.sendBack')}
                                </button>
                            </div>
                        </Section>

                        <div className="flex gap-1.5">
                            <button
                                type="button"
                                onClick={() => duplicateObject(uid, object.id)}
                                className="flex h-[34px] flex-1 items-center justify-center gap-1.5 rounded-[9px] border border-[#E2DCD6] bg-white text-[12px] font-semibold text-[#3D3833] transition-colors hover:border-primary/50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                            >
                                <IconCopy size={14} stroke={1.8} />
                                {t('common.copy')}
                            </button>
                            <button
                                type="button"
                                onClick={() => removeObject(uid, object.id)}
                                className="flex h-[34px] flex-1 items-center justify-center gap-1.5 rounded-[9px] border border-[#FADBD5] bg-[#FDECEA] text-[12px] font-semibold text-[#C0392B] transition-colors hover:bg-[#FBDDD8] dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-400"
                            >
                                <IconTrash size={14} stroke={1.8} />
                                {t('common.delete')}
                            </button>
                        </div>
                    </>
                ) : (
                    <p className="text-[11.5px] leading-[1.6] text-[#A8A099] dark:text-zinc-500">
                        {t('pdfEditor.inspector.empty')}
                    </p>
                )}
            </div>
        </aside>
    );
}
